/**
 * PWA Install — Global event capture
 *
 * Captures the beforeinstallprompt event at module load time so it's
 * available to any component, regardless of when it mounts.
 */

export interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;
const listeners = new Set<(e: BeforeInstallPromptEvent | null) => void>();

function notify() {
    for (const fn of listeners) fn(deferredPrompt);
}

// Capture globally — runs once at import time
window.addEventListener("beforeinstallprompt", (e: Event) => {
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
    notify();
});

window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    notify();
});

export function getInstallPrompt(): BeforeInstallPromptEvent | null {
    return deferredPrompt;
}

export function clearInstallPrompt(): void {
    deferredPrompt = null;
    notify();
}

export function onInstallPromptChange(fn: (e: BeforeInstallPromptEvent | null) => void): () => void {
    listeners.add(fn);
    return () => listeners.delete(fn);
}

export function isIos(): boolean {
    const ua = navigator.userAgent;
    const isIosUA = /iphone|ipad|ipod/i.test(ua);
    const isIpadOS = /macintosh/i.test(ua) && navigator.maxTouchPoints > 1;
    return isIosUA || isIpadOS;
}

export function isInStandaloneMode(): boolean {
    return (
        window.matchMedia("(display-mode: standalone)").matches ||
        ("standalone" in navigator && (navigator as Record<string, unknown>).standalone === true)
    );
}
